/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { setReviewMediaGalleryOpen } from "@Pages/PropertyPage/data/propertyPageReducer";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import Portal from "@Components/Portal";
import Modal from "@Components/Modal";
import useIsDesktop from "hooks/useIsDesktop";
import { useHistory } from "hooks/useHistory";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import RatingOverview from "./components/RatingOverview";
import ReviewCardsList from "./components/ReviewCardsList";
import ReviewsPanel from "./components/ReviewsPanel";
import { buildReviewsData } from "./buildReviewsData";
import { trackParameterDrawerExpanded } from "./analytics";
import styles from "./reviewsSectionV2.module.scss";
import ReviewMediaGallery from "./components/ReviewMediaGallery";

const ReviewsSectionV2: React.FC<any> = () => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [scrollToReviewId, setScrollToReviewId] = useState<string | null>(null);
  // @ts-ignore
  const [galleryIndex, setGalleryIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedView = useRef(false);
  const { t } = useTranslationHook();
  const isDesktop = useIsDesktop();
  const history = useHistory();
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);

  // Listen for external "open reviews panel" event (e.g. from star rating click)
  useEffect(() => {
    const handleOpenPanel = () => setIsPanelOpen(true);
    const handleClosePanel = () => {
      setIsPanelOpen(false);
      setScrollToReviewId(null);
    };
    window.addEventListener("open-reviews-panel", handleOpenPanel);
    window.addEventListener("close-reviews-panel", handleClosePanel);
    return () => {
      window.removeEventListener("open-reviews-panel", handleOpenPanel);
      window.removeEventListener("close-reviews-panel", handleClosePanel);
    };
  }, []);

  // Deep link from search hover "Read more": ?open-reviews=true
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("open-reviews") !== "true") return;

    setIsPanelOpen(true);
    params.delete("open-reviews");
    const qs = params.toString();
    const newURL = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
    window.history.replaceState({ ...window.history.state, path: newURL }, "", newURL);
  }, []);

  const {
    reviewsRating,
    reviewSummary,
    rawReviews,
    propertyName,
    propertyId,
    cityId,
    isGalleryOpen,
  } = useSelector(
    ({ propertyPageData }: AppState) => ({
      reviewsRating: propertyPageData?.data?.reviews_rating,
      reviewSummary: propertyPageData?.data?.meta?.review_summary,
      rawReviews: propertyPageData?.inventoryReviewData,
      propertyName: propertyPageData?.data?.name || propertyPageData?.data?.region?.name || "",
      propertyId: propertyPageData?.data?.id || "",
      cityId: propertyPageData?.data?.region_id || propertyPageData?.data?.region?.id || "",
      isGalleryOpen: propertyPageData?.review_media_gallery_open || false,
    }),
    shallowEqual,
  );
  const isOldMediaPage = false;

  const {
    overallRating,
    ratingLabel,
    reviewCount,
    categories,
    summary,
    photos,
    reviews,
    mediaArray,
    mediaIndexMap,
  } = useMemo(
    () => buildReviewsData({ reviewsRating, reviewSummary, rawReviews, propertyName }),
    [reviewsRating, reviewSummary, rawReviews, propertyName],
  );

  // Track review_section_viewed when >= 50% visible
  useEffect(() => {
    if (typeof window === "undefined" || !sectionRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true;
          // trackReviewSectionViewed({
          //   propertyId,
          //   cityId,
          //   totalReviewCount: reviewCount,
          //   aiSummaryRendered: Boolean(summary),
          // });
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [propertyId, cityId, reviewCount, summary]);

  // Track parameter_drawer_expanded when reviews panel is opened
  useEffect(() => {
    if (isPanelOpen && propertyId) {
      trackParameterDrawerExpanded({
        propertyId,
        initialVisibleParameters: categories.map((c) => c.label),
      });
    }
  }, [isPanelOpen]);

  const handleMediaClick = (index: number) => {
    if (isOldMediaPage) {
      // Use normal gallery carousel for both mobile and desktop for now
      propertyPageDispatcher.showGalleryCarousel({
        show: true,
        images: mediaArray,
        reviewSection: true,
        currentItemIndex: index,
        showViewRoomButton: false,
      });
    } else {
      // TODO: Re-enable ReviewMediaGallery for desktop when ready
      setGalleryIndex(index);
      dispatch(setReviewMediaGalleryOpen(true));
    }
  };

  const handleReadMore = (reviewId: string) => {
    setScrollToReviewId(reviewId);
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
    setScrollToReviewId(null);
  };

  // Notify SectionNavigation to re-scan DOM when this section mounts/unmounts
  useEffect(() => {
    if (typeof window === "undefined") return;
    const event = new Event("navigation_section_removed");
    window.dispatchEvent(event);
  }, [reviewCount, summary]);

  if (reviewCount === 0 && !summary) return null;

  return (
    <section className={styles.container} id="reviews" ref={sectionRef}>
      <div className={styles.header}>
        <h3 className={styles.headerTitle}>{t("propertypage.reviews.title")}</h3>
      </div>

      <div className={styles.content}>
        <RatingOverview
          overallRating={overallRating}
          ratingLabel={ratingLabel}
          reviewCount={reviewCount}
          categories={categories}
          summary={summary}
          photos={photos}
          onMediaClick={handleMediaClick}
          propertyId={propertyId}
        />
      </div>

      {reviews.length > 0 && (
        <div className={styles.reviewCardsSection}>
          <ReviewCardsList
            reviews={reviews}
            onMediaClick={handleMediaClick}
            onReadMore={handleReadMore}
            mediaIndexMap={mediaIndexMap}
            isDesktop={isDesktop}
            propertyId={propertyId}
          />
        </div>
      )}

      {reviewCount > 0 && (
        <div className={styles.footer}>
          <button
            className={styles.footerLink}
            type="button"
            aria-label={t("propertypage.reviews.readall")}
            onClick={() => setIsPanelOpen(true)}
          >
            {t("propertypage.reviews.readall")}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 4L10 8L6 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

      {isPanelOpen && (
        <ReviewsPanel
          isOpen={isPanelOpen}
          onClose={handleClosePanel}
          overallRating={overallRating}
          ratingLabel={ratingLabel}
          reviewCount={reviewCount}
          categories={categories}
          summary={summary}
          photos={photos}
          reviews={reviews}
          onMediaClick={handleMediaClick}
          scrollToReviewId={scrollToReviewId}
          mediaIndexMap={mediaIndexMap}
          history={history}
        />
      )}

      {isGalleryOpen && (
        <Portal rootId="forPortal">
          <Modal
            hideModal={() => dispatch(setReviewMediaGalleryOpen(false))}
            size="fullScreen"
            hideCloseButton
            isHistoryPush={!isDesktop}
            type="reviewMediaGallery"
            animation="bottomToTop"
          >
            <ReviewMediaGallery
              media={mediaArray}
              initialIndex={galleryIndex}
              isOpen={isGalleryOpen}
              onClose={() => {
                dispatch(setReviewMediaGalleryOpen(false));
              }}
              isDesktop={isDesktop}
              reviews={reviews}
              mediaIndexMap={mediaIndexMap}
              propertyName={propertyName}
              propertyId={propertyId}
            />
          </Modal>
        </Portal>
      )}
    </section>
  );
};

export default wrapperHOC(ReviewsSectionV2, {
  componentName: "ReviewsSectionV2",
  showForChina: true,
  showForWhiteLable: true,
});
